import * as THREE from 'three'
import { Sky } from 'three/examples/jsm/objects/Sky.js'

export interface SkyResult {
  sky: Sky
  sun: THREE.DirectionalLight
  hemi: THREE.HemisphereLight
  sunDir: THREE.Vector3
  /** Boja horizonta — koristi se za maglu i za fresnel vode. */
  horizon: THREE.Color
  setSun(elevacijaStepeni: number, azimutStepeni: number): void
  refreshEnv(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void
  dispose(): void
}

export function buildSky(mapSize: number): SkyResult {
  const sky = new Sky()
  sky.scale.setScalar(mapSize * 40)
  const u = sky.material.uniforms
  u.turbidity.value = 4.2
  u.rayleigh.value = 1.6
  u.mieCoefficient.value = 0.006
  u.mieDirectionalG.value = 0.82

  const sunDir = new THREE.Vector3()

  const sun = new THREE.DirectionalLight(0xfff0d8, 1.8)
  sun.castShadow = true
  sun.shadow.mapSize.set(4096, 4096)
  const rub = mapSize * 0.62
  const cam = sun.shadow.camera
  cam.left = -rub
  cam.right = rub
  cam.top = rub
  cam.bottom = -rub
  cam.near = mapSize * 0.2
  cam.far = mapSize * 3.2
  cam.updateProjectionMatrix()
  sun.shadow.bias = -0.0006
  sun.shadow.normalBias = 1.6
  sun.target.position.set(0, 0, 0)

  const hemi = new THREE.HemisphereLight(0xbcd9f2, 0x54503f, 0.42)

  const horizon = new THREE.Color('#9fc4dd')
  let pmrem: THREE.PMREMGenerator | null = null
  let env: THREE.Texture | null = null

  function setSun(elevacija: number, azimut: number): void {
    const phi = THREE.MathUtils.degToRad(90 - elevacija)
    const theta = THREE.MathUtils.degToRad(azimut)
    sunDir.setFromSphericalCoords(1, phi, theta)
    u.sunPosition.value.copy(sunDir)

    sun.position.copy(sunDir).multiplyScalar(mapSize * 1.5)
    sun.target.updateMatrixWorld()

    // Toplija i slabija svetlost što je sunce niže — kasno popodne izgleda najbolje.
    const nisko = THREE.MathUtils.clamp(1 - elevacija / 40, 0, 1)
    sun.color.setHSL(0.09, 0.35 + nisko * 0.35, 0.72 - nisko * 0.1)
    sun.intensity = 1.05 + (1 - nisko) * 0.95
    horizon.setHSL(0.57 - nisko * 0.04, 0.32 + nisko * 0.18, 0.62 - nisko * 0.1)
  }

  setSun(24, 128)

  return {
    sky,
    sun,
    hemi,
    sunDir,
    horizon,
    setSun,
    refreshEnv(renderer, scene) {
      if (!pmrem) pmrem = new THREE.PMREMGenerator(renderer)
      env?.dispose()
      const scenaNeba = new THREE.Scene()
      scenaNeba.add(sky)
      env = pmrem.fromScene(scenaNeba).texture
      scene.add(sky)
      scene.environment = env
      scene.environmentIntensity = 0.32
    },
    dispose() {
      sky.geometry.dispose()
      sky.material.dispose()
      env?.dispose()
      pmrem?.dispose()
    },
  }
}
